/**
 * Admin-only user management page.
 * Supports CRUD operations for users and navigation to API key management.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Key as KeyIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { UserResponse, UserCreateRequest, UserUpdateRequest, ApiKeyResponse } from '../services/api.service';

export interface ApiKeyFormData {
  neolms: string;
  wela: string;
}

interface UserFormData {
  username: string;
  password: string;
  confirmPassword: string;
  is_admin: boolean;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user, users, apiKeys, loading, getAllUsers, createUser, updateUser, deleteUser, updateApiKey, updateUserApiKey, getUserApiKeys } = useAuth();
  const usersLoadedRef = useRef(false);
  const isInitialMount = useRef(true);
  
  // Current user being edited
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  
  // Forms data
  const [userFormData, setUserFormData] = useState<UserFormData>({
    username: '',
    password: '',
    confirmPassword: '',
    is_admin: false,
  });
  
  const [passwordFormData, setPasswordFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  
  // UI state
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // Dialog states
  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);

  // Effects
  useEffect(() => {
    // Only run on first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      
      // If not admin, redirect
      if (user && !user.is_admin) {
        navigate('/api-status');
        return;
      }
      
      // If we're an admin and users array is empty, fetch them
      if (user?.is_admin && users.length === 0 && !usersLoadedRef.current) {
        usersLoadedRef.current = true; // Mark as loading to prevent duplicate requests
        
        getAllUsers().catch(err => {
          console.error('Failed to load users:', err);
          setErrorMessage('Failed to load users');
          usersLoadedRef.current = false; // Reset if there was an error
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);
  
  // If user is not admin, don't render this page
  if (user && !user.is_admin) {
    return null;
  }
  
  // Show loading indicator
  if (loading && users.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5">Loading users...</Typography>
      </Box>
    );
  }
  
  // Event handlers
  const handleCreateUser = async () => {
    // Validate
    if (!userFormData.username) {
      setErrorMessage('Username is required');
      return;
    }
    
    if (!userFormData.password) {
      setErrorMessage('Password is required');
      return;
    }
    
    if (userFormData.password !== userFormData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    
    try {
      setErrorMessage('');
      
      const newUserData: UserCreateRequest = {
        username: userFormData.username,
        password: userFormData.password,
        is_admin: userFormData.is_admin
      };
      
      await createUser(newUserData);
      setSuccessMessage('User created successfully');
      setOpenCreateUserDialog(false);
      
      // Reset form
      setUserFormData({
        username: '',
        password: '',
        confirmPassword: '',
        is_admin: false,
      });
    } catch (error) {
      setErrorMessage('Failed to create user');
    }
  };
  
  const handleUpdatePassword = async () => {
    if (!currentUserId) return;
    
    // Validate
    if (!passwordFormData.password) {
      setErrorMessage('Password is required');
      return;
    }
    
    if (passwordFormData.password !== passwordFormData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    
    try {
      setErrorMessage('');
      
      const updateData: UserUpdateRequest = {
        password: passwordFormData.password
      };
      
      await updateUser(currentUserId, updateData);
      setSuccessMessage('Password updated successfully');
      setOpenPasswordDialog(false);
      
      // Reset form
      setPasswordFormData({
        password: '',
        confirmPassword: '',
      });
    } catch (error) {
      setErrorMessage('Failed to update password');
    }
  };
  
  const handleToggleAdmin = async (userId: number, isCurrentlyAdmin: boolean) => {
    try {
      setErrorMessage('');
      
      const updateData: UserUpdateRequest = {
        is_admin: !isCurrentlyAdmin
      };
      
      await updateUser(userId, updateData);
      setSuccessMessage(`User admin status updated`);
    } catch (error) {
      setErrorMessage('Failed to update user admin status');
    }
  };
  
  const handleDeleteUser = async () => {
    if (!currentUserId) return;
    
    try {
      setErrorMessage('');
      await deleteUser(currentUserId);
      setSuccessMessage('User deleted successfully');
      setOpenDeleteDialog(false);
    } catch (error) {
      setErrorMessage('Failed to delete user');
    }
  };
  
  const openPasswordDialogForUser = (userId: number) => {
    setCurrentUserId(userId);
    setPasswordFormData({
      password: '',
      confirmPassword: '',
    });
    setOpenPasswordDialog(true);
  };
  
  const openApiKeyDialogForUser = (userId: number) => {
    console.log(`Navigating to API key management for user ID: ${userId}`);
    navigate(`/users/${userId}/api-keys`);
  };
  
  const openDeleteDialogForUser = (userId: number) => {
    setCurrentUserId(userId);
    setOpenDeleteDialog(true);
  };
  
  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      
      <Box sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateUserDialog(true)}
        >
          Add New User
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>
                  <Chip 
                    label={user.is_admin ? 'Administrator' : 'User'}
                    color={user.is_admin ? 'primary' : 'default'}
                    onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Change Password">
                    <IconButton 
                      aria-label="change password"
                      onClick={() => openPasswordDialogForUser(user.id)}
                    >
                      <LockIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Manage API Keys">
                    <IconButton 
                      aria-label="manage API keys"
                      onClick={(e) => {
                        console.log('API Key button clicked for user ID:', user.id);
                        e.stopPropagation(); // Prevent event bubbling
                        openApiKeyDialogForUser(user.id);
                      }}
                    >
                      <KeyIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete User">
                    <IconButton 
                      aria-label="delete user"
                      color="error"
                      onClick={() => openDeleteDialogForUser(user.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Create User Dialog */}
      <Dialog open={openCreateUserDialog} onClose={() => setOpenCreateUserDialog(false)}>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            type="text"
            fullWidth
            variant="outlined"
            value={userFormData.username}
            onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={userFormData.password}
            onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Confirm Password"
            type="password"
            fullWidth
            variant="outlined"
            value={userFormData.confirmPassword}
            onChange={(e) => setUserFormData({ ...userFormData, confirmPassword: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={userFormData.is_admin}
                onChange={(e) => setUserFormData({ ...userFormData, is_admin: e.target.checked })}
              />
            }
            label="Administrator"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateUserDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained" color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Change Password Dialog */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordFormData.password}
            onChange={(e) => setPasswordFormData({ ...passwordFormData, password: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordFormData.confirmPassword}
            onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdatePassword} variant="contained" color="primary">
            Update Password
          </Button>
        </DialogActions>
      </Dialog>
       
      {/* Delete User Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 